export const TOP_64_OFFICIAL_RANKING_ID = "top-64-hockey-pipeline-2026";

export const TOP_64_COMPETITION_NAME =
  "Top 64 - Hockey Pipeline 2026 Ranking Competition";

export const TOP_64_COMPETITION_SHORT_LABEL = "Top 64 Competition";

/** Default title for a new competition entry (e.g. "Jane Doe's Top 64 - 2026 Draft"). */
export function defaultTop64CompetitionTitle(user) {
  const first = user?.first_name?.trim();
  const last = user?.last_name?.trim();
  const name =
    [first, last].filter(Boolean).join(" ") ||
    user?.name?.trim() ||
    null;
  if (!name) return `Top 64 - ${TOP_64_DRAFT_YEAR} Draft`;
  return `${name}'s Top 64 - ${TOP_64_DRAFT_YEAR} Draft`;
}

export const TOP_64_PLAYER_COUNT = 64;

export const TOP_64_DRAFT_YEAR = 2026;

export const TOP_64_RULES_PATH = "/top-64-competition";

export const TOP_64_CREATE_PATH = `/your-rankings/create?competition=${TOP_64_OFFICIAL_RANKING_ID}`;

/** Deadline shown to users (7:00 PM Eastern on June 26, 2026). */
export const TOP_64_DEADLINE_LABEL = "June 26, 2026 at 7:00 PM ET";

/** Compact deadline for narrow promo surfaces (e.g. home page card). */
export const TOP_64_DEADLINE_SHORT = "Jun 26, 2026 · 7 PM ET";

export const TOP_64_PRIZE_USD = 50;

export const TOP_64_COMPETITION_GIF_URL =
  "https://media.giphy.com/media/hrQnFqUKTXwSSvH8AI/giphy.gif";

/** Max points earned per drafted player on an exact pick. */
export const TOP_64_MAX_POINTS_PER_PLAYER = 10;

/** Tiered scoring bands shown on the rules page. */
export const TOP_64_SCORING_TIERS = [
  { label: "Exact pick", detail: "Your rank matches the actual pick", points: 10 },
  { label: "Within 1 pick", detail: "Off by 1", points: 8 },
  { label: "Within 2 picks", detail: "Off by 2", points: 6 },
  { label: "Within 3 picks", detail: "Off by 3", points: 4 },
  { label: "Within 5 picks", detail: "Off by 4 or 5", points: 2 },
  {
    label: "On your board",
    detail: "On your board, off by 6 or more picks",
    points: 1,
  },
];

/**
 * Tiered proximity scoring for each player drafted in the top 64.
 * Players drafted but missing from your board score 0.
 */
export function top64PlayerPoints(predictedRank, actualPick) {
  if (predictedRank == null || actualPick == null) return 0;
  const error = Math.abs(predictedRank - actualPick);
  if (error === 0) return 10;
  if (error === 1) return 8;
  if (error === 2) return 6;
  if (error === 3) return 4;
  if (error === 4 || error === 5) return 2;
  return 1;
}

const SCORING_EXAMPLE_ROWS = [
  { label: "Exact pick", predicted: 1, actual: 1 },
  { label: "Within 1 pick", predicted: 4, actual: 5 },
  { label: "Within 2 picks", predicted: 10, actual: 12 },
  { label: "Within 3 picks", predicted: 20, actual: 23 },
  { label: "Within 5 picks", predicted: 10, actual: 14 },
  { label: "On your board", predicted: 8, actual: 45 },
  { label: "Not on your board", predicted: null, actual: 18 },
];

export const TOP_64_SCORING_EXAMPLES = SCORING_EXAMPLE_ROWS.map((row) => ({
  ...row,
  points: top64PlayerPoints(row.predicted, row.actual),
}));

const TIER_SUMMARY_ORDER = [10, 8, 6, 4, 2, 1, 0];

const TIER_SUMMARY_PHRASES = {
  10: { one: "exact pick", other: "exact picks" },
  8: { one: "off by 1", other: "off by 1" },
  6: { one: "off by 2", other: "off by 2" },
  4: { one: "off by 3", other: "off by 3" },
  2: { one: "off by 4–5", other: "off by 4–5" },
  1: { one: "off by 6+", other: "off by 6+" },
  0: { one: "not on board", other: "not on board" },
};

/** Count how many drafted picks landed in each scoring band. */
export function summarizeTop64ScoreBreakdown(breakdown = []) {
  const counts = { 10: 0, 8: 0, 6: 0, 4: 0, 2: 0, 1: 0, 0: 0 };
  for (const row of breakdown) {
    const pts = row.points ?? 0;
    if (Object.prototype.hasOwnProperty.call(counts, pts)) {
      counts[pts] += 1;
    } else {
      counts[0] += 1;
    }
  }

  return TIER_SUMMARY_ORDER.map((points) => ({
    points,
    count: counts[points],
    phrase: TIER_SUMMARY_PHRASES[points],
  })).filter((tier) => tier.count > 0);
}

export function formatTop64TierCount({ count, phrase }) {
  const label = count === 1 ? phrase.one : phrase.other;
  return label;
}
